// Parses a video packet from the app.
//
// Wire format: the 4-byte ASCII magic "flex" followed by a flexbuffer holding the
// positional vector [metadataMap, packetBlob]. The C++ side is
// StreamBGFXPacketMetadata.
//
// Optional fields get the SAME defaults the native client uses
// (StreamBGFXPacketMetadata.cpp), which matters for two reasons: the flexbuffers
// reader throws on a missing map key rather than returning undefined, and older
// apps genuinely do not send some of these. A missing isIDRFrame in particular
// defaults to TRUE, so an old app cannot leave the client requesting key frames
// forever.
import {toReference} from "flatbuffers/js/flexbuffers/reference.js";

const MAGIC = "flex";

/**
 * @param {any} map a flexbuffers map reference
 * @param {string} key
 * @param {(ref: any) => any} read
 * @param {any} fallback used when the key is absent
 */
function getOr(map, key, read, fallback) {
	let ref;
	try {
		ref = map.get(key);
	} catch {
		// flexbuffers throws for an absent key rather than returning undefined.
		return fallback;
	}
	// Only the lookup is guarded: a throw from read() means the buffer is damaged,
	// and it must propagate so the packet is rejected rather than silently decoded
	// with a default in place of real data.
	const value = read(ref);
	return value == null ? fallback : value;
}

/** True if the buffer carries the flexbuffer magic. */
export function hasFlexMagic(buffer) {
	// The magic alone is not a packet: flexbuffers needs a root, so anything this
	// short would throw inside toReference().
	if (!buffer || buffer.byteLength < MAGIC.length + 3) return false;
	const head = new Uint8Array(buffer, 0, MAGIC.length);
	for (let i = 0; i < MAGIC.length; i++) {
		if (head[i] !== MAGIC.charCodeAt(i)) return false;
	}
	return true;
}

/**
 * @typedef {object} VideoPacket
 * @property {number} codec
 * @property {number} version serialisation version, echoed back in input
 * @property {number} packetNumber monotonic; a gap means frames were lost
 * @property {number} frameTime sender clock, microseconds
 * @property {boolean} isIDR
 * @property {number} width
 * @property {number} height
 * @property {number|null} userToMakeViewOnly
 * @property {string|null} clipboardText the app's clipboard, sent only when it changes
 * @property {{id: number, name: string, viewOnly: boolean, cursor: {x: number, y: number}|null}[]|null} users null when the app did not report presence
 * @property {{codecId: number, channels: number, sampleRate: number}|null} audioParams what the app's encoder is set to (AudioCodecParams); sent on every packet, audio or not
 * @property {Uint8Array[]} audio encoded audio frames riding on this packet, usually none
 * @property {number} messageBytes size of the whole websocket message
 * @property {Uint8Array} data the encoded frame
 */

/**
 * @param {ArrayBuffer} buffer one whole websocket message
 * @returns {VideoPacket|null} null if this is not a video packet
 */
export function parseVideoPacket(buffer) {
	if (!hasFlexMagic(buffer)) return null;

	// No slice: the flexbuffer root is located from the END of the buffer, so the 4
	// magic bytes at the front are simply never referenced. Slicing them off copied
	// the entire message on every frame — ~15KB at 60fps for nothing. (Verified by
	// parsing the same buffer both ways.)
	const root = toReference(buffer);
	// The library types Reference.get() for vector indices only; this is a map.
	const meta = /** @type {any} */ (root.get(0));

	// null means the app did not report presence at all, which is different from
	// reporting that nobody is connected.
	let users = null;
	const userInfo = getOr(meta, "userInfo", (ref) => ref, null);
	if (userInfo) {
		users = [];
		for (let i = 0; i < userInfo.length(); i++) {
			const user = userInfo.get(i);
			let cursor = null;
			const history = getOr(user, "mouseHistory", (ref) => ref, null);
			if (history && history.length() > 0) {
				// Only the latest position matters for drawing a remote cursor; the
				// rest of the history is for annotation trails, which are not drawn.
				const latest = history.get(history.length() - 1);
				cursor = {x: latest.get(1).floatValue(), y: latest.get(2).floatValue()};
			}
			users.push({
				id: Number(user.get("uniqueId").intValue()),
				name: user.get("name").stringValue(),
				viewOnly: getOr(user, "viewOnly", (ref) => ref.boolValue(), false),
				cursor,
			});
		}
	}

	// Absent optionals are encoded as an empty vector, matching the C++
	// std::optional serialisation.
	const viewOnlyTarget = getOr(meta, "userToMakeViewOnly", (ref) => ref, null);
	const clipboard = getOr(meta, "clipboardText", (ref) => ref, null);

	// AudioCodecParams is the positional vector [codecId, numChannels,
	// channelLayout, sampleRate, format]; audioData a vector of blobs, one per
	// encoded frame, attached to the first video packet after they were encoded.
	const params = getOr(meta, "audioCodecParams", (ref) => ref, null);
	const audioParams =
		params && params.length() >= 4
			? {
					codecId: Number(params.get(0).intValue()),
					channels: Number(params.get(1).intValue()),
					sampleRate: Number(params.get(3).intValue()),
				}
			: null;
	const audio = [];
	const audioData = getOr(meta, "audioData", (ref) => ref, null);
	if (audioData) {
		for (let i = 0; i < audioData.length(); i++) audio.push(audioData.get(i).blobValue());
	}

	return {
		codec: getOr(meta, "codec", (ref) => Number(ref.intValue()), 0),
		// Missing version means the oldest serialisation.
		version: getOr(meta, "version", (ref) => Number(ref.intValue()), 1),
		packetNumber: getOr(meta, "packetNumber", (ref) => Number(ref.intValue()), 0),
		// Missing frameTime means "now": a latency of zero is less misleading than a
		// wildly wrong one.
		frameTime: getOr(meta, "frameTime", (ref) => Number(ref.intValue()), 0),
		// Defaults to TRUE so an app that does not send it cannot leave the client
		// waiting for a key frame that never comes.
		isIDR: getOr(meta, "isIDRFrame", (ref) => ref.boolValue(), true),
		width: getOr(meta, "width", (ref) => Number(ref.intValue()), 0),
		height: getOr(meta, "height", (ref) => Number(ref.intValue()), 0),
		userToMakeViewOnly:
			viewOnlyTarget && viewOnlyTarget.length() > 0
				? Number(viewOnlyTarget.get(0).intValue())
				: null,
		clipboardText: clipboard && clipboard.length() > 0 ? clipboard.get(0).stringValue() : null,
		users,
		audioParams,
		audio,
		messageBytes: buffer.byteLength,
		data: root.get(1).blobValue(),
	};
}
